import { Component } from 'react';
import styles from '../../styles/amcs.scss';
import { SubHeading } from '../common';

interface SimilarFund {
  productId: string;
  name: string;
  image: string;
  cagr5Y: number;
  expenseRatio: number;
  isinCode: string;
  risk: string;
  rating: number;
  category: string;
  nav: number;
  navChangePercent: number;
  slug: string;
  parentSlug: string;
  exitLoad: number;
  return1M: number;
  return3M: number;
  return6M: number;
  return1Y: number;
  return3Y: number;
  return5Y: number;
  returnALL: number;
  aum: number;
  alpha1Y: number | null;
  alpha3Y: number | null;
  alpha5Y: number | null;
}

interface PeerComparisonProps {
  similarFunds: SimilarFund[];
}

interface PeerComparisonState {
  selectedTab: 'overview' | 'returns' | 'risk';
}

class PeerComparison extends Component<
  PeerComparisonProps,
  PeerComparisonState
> {
  constructor(props: PeerComparisonProps) {
    super(props);
    this.state = {
      selectedTab: 'overview',
    };
  }

  public switchBtn = (tab: 'overview' | 'returns' | 'risk') => {
    this.setState({ selectedTab: tab });
  };

  public tabSelector = () => {
    const { selectedTab } = this.state;
    return (
      <div className="lil-flex lil-justify-left sm:lil-mx-6">
        <div
          id="switchBtnHolder1"
          onClick={() => this.switchBtn('overview')}
          className={`lil-w-1/5 lil-text-center lil-cursor-pointer lil-font-semibold lil-py-2 sm:lil-w-1/2
                      ${
                        selectedTab === 'overview'
                          ? `lil-border-b-lg ${styles['border-blue-250']} lil-bg-blue-50 `
                          : ' '
                      }`}
        >
          Overview
        </div>
        <div
          id="switchBtnHolder2"
          onClick={() => this.switchBtn('returns')}
          className={`lil-w-1/5 lil-text-center lil-cursor-pointer lil-font-semibold lil-py-2 sm:lil-w-1/2
                      ${
                        selectedTab === 'returns'
                          ? `lil-border-b-lg ${styles['border-blue-250']} lil-bg-blue-50 `
                          : ' '
                      }`}
        >
          Returns
        </div>
        <div
          id="switchBtnHolder2"
          onClick={() => this.switchBtn('risk')}
          className={`lil-w-1/5 lil-text-center lil-cursor-pointer lil-font-semibold lil-py-2 sm:lil-w-1/2
                      ${
                        selectedTab === 'risk'
                          ? `lil-border-b-lg ${styles['border-blue-250']} lil-bg-blue-50 `
                          : ' '
                      }`}
        >
          Risk Ratios
        </div>
      </div>
    );
  };

  public formatPercentage = (value: number | null) => {
    if (value === null) {
      return 'N/A';
    }
    return `${value.toFixed(2)}%`;
  };

  public formatNumber = (value: number | null) => {
    if (value === null) {
      return '--';
    }
    return `${value.toFixed(2)}`;
  };

  public formatCurrency = (value: number) => {
    // Format AUM in crores
    const crores = value / 10000000;
    // Format with Indian numbering system using locale
    return (
      crores
        .toLocaleString('en-IN', {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
          style: 'currency',
          currency: 'INR',
          currencyDisplay: 'symbol',
        })
        .replace('₹', '₹ ') + ' Cr'
    );
  };

  public formatNav = (nav: number, changePercent: number) => {
    const changeClass =
      changePercent >= 0 ? 'lil-text-green-450' : 'lil-text-red';
    return (
      <div>
        <span>₹{nav.toFixed(2)}</span>
        {' | '}
        <span className={changeClass}>{changePercent.toFixed(2)}%</span>
      </div>
    );
  };

  public formatFundName = (name: string, fund: SimilarFund) => {
    const maxLength = 25;
    const displayName =
      name.length <= maxLength ? name : `${name.substring(0, maxLength)}...`;

    const fundUrl = `/mutual-funds/${fund.parentSlug}/${fund.slug}/${fund.isinCode}`;

    return (
      <a href={fundUrl} className="lil-hover:underline" title={name}>
        {displayName}
      </a>
    );
  };

  public formatRisk = (risk: string) => {
    return risk.replace(' Risk', '');
  };

  public renderTable = () => {
    const { similarFunds } = this.props;
    const { selectedTab } = this.state;
    if (!similarFunds || similarFunds.length === 0) {
      return <div className="lil-mt-6">No similar funds available.</div>;
    }
    const displayedFunds = similarFunds.slice(0, 4);
    let columns = [];
    if (selectedTab === 'overview') {
      columns = [
        {
          title: 'Fund Name',
          key: 'name',
          className: 'lil-w-3/6',
          formatter: this.formatFundName,
        },
        {
          title: 'AUM (In Crs.)',
          key: 'aum',
          className: 'lil-w-1/6',
          formatter: this.formatCurrency,
        },
        {
          title: 'NAV',
          key: 'nav',
          className: 'lil-w-2/6 lil-whitespace-no-wrap lil-pl-12',
          formatter: (nav: number, fund: SimilarFund) =>
            this.formatNav(nav, fund.navChangePercent),
        },
        {
          title: 'Risk',
          key: 'risk',
          className: 'lil-w-1/6',
          formatter: this.formatRisk,
        },
      ];
    } else if (selectedTab === 'returns') {
      columns = [
        {
          title: 'Fund Name',
          key: 'name',
          className: 'lil-w-3/6',
          formatter: this.formatFundName,
        },
        {
          title: '1M',
          key: 'return1M',
          className: 'lil-w-1/6',
          formatter: this.formatPercentage,
        },
        {
          title: '6M',
          key: 'return6M',
          className: 'lil-w-1/6',
          formatter: this.formatPercentage,
        },
        {
          title: '1Y',
          key: 'return1Y',
          className: 'lil-w-1/6',
          formatter: this.formatPercentage,
        },
        {
          title: '3Y',
          key: 'return3Y',
          className: 'lil-w-1/6',
          formatter: this.formatPercentage,
        },
      ];
    } else {
      columns = [
        {
          title: 'Fund Name',
          key: 'name',
          className: 'lil-w-3/6',
          formatter: this.formatFundName,
        },
        {
          title: 'Alpha',
          key: 'alpha1Y',
          className: 'lil-w-1/6',
          formatter: this.formatNumber,
        },
        {
          title: 'Beta',
          key: 'alpha3Y',
          className: 'lil-w-1/6',
          formatter: this.formatNumber,
        },
        {
          title: 'Sharpe',
          key: 'alpha3Y',
          className: 'lil-w-1/6',
          formatter: this.formatNumber,
        },
        {
          title: 'Sortino',
          key: 'alpha3Y',
          className: 'lil-w-1/6',
          formatter: this.formatNumber,
        },
      ];
    }
    return (
      <div className="lil-mt-4 lil-w-full">
        <div
          className="lil-overflow-x-auto lil-scrolling-touch"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <table
            className="lil-text-s-18 lil-border-separate lil-w-full sm:lil-px-6"
            style={{
              tableLayout: 'fixed',
              minWidth: '700px',
            }}
          >
            <thead>
              <tr>
                {columns.map((col, index) => (
                  <th
                    key={index}
                    className={`lil-border-none lil-text-left ${col.className}`}
                  >
                    {col.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedFunds.map((fund, rowIndex) => (
                <tr key={fund.productId} className="lil-relative">
                  {columns.map((col, colIndex) => {
                    const value = fund[col.key as keyof SimilarFund];
                    return (
                      <td
                        key={colIndex}
                        className={`lil-border-none lil-font-light lil-py-4 lil-px-2 ${col.className} lil-whitespace-no-wrap lil-overflow-hidden lil-text-ellipsis`}
                      >
                        {col.formatter
                          ? col.formatter(value as any, fund)
                          : value}
                      </td>
                    );
                  })}
                  {rowIndex !== displayedFunds.length - 1 && (
                    <div className="lil-absolute lil-h-px lil-bg-grey-300 lil-bottom-0 lil-left-0 lil-right-0"></div>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  render() {
    return (
      <div className={`lil-mt-10 sm:lil--mx-6 sm:lil-mt-8`}>
        <div className="sm:lil-ml-6">
          <SubHeading title="Peer Comparison" />
        </div>

        <div className="lil-py-6">
          {this.tabSelector()}
          {this.renderTable()}
        </div>
      </div>
    );
  }
}

export default PeerComparison;



// WEBPACK FOOTER //
// ./components/singleFundNewCmp/peerComparison.tsx